import { supabase, TABLES } from '../../config/supabase';
import {
  AssetAllocation,
  AssetType,
  AuthError,
  GoalSummary,
  PerformanceDataPoint,
} from '../../types';

export interface GoalAnalytics {
  goal: GoalSummary;
  totalValue: number;
  totalContributions: number;
  totalGainLoss: number;
  totalGainLossPercentage: number;
  progressToTarget: number;
  monthlyProgressRate: number;
  projectedCompletionDate?: string;
  accountCount: number;
  lastUpdated: string;
}

export interface GoalPerformanceData {
  goalId: string;
  timeRange: '1D' | '1W' | '1M' | '3M' | '6M' | '1Y' | 'ALL';
  data: PerformanceDataPoint[];
  totalReturn: number;
  totalReturnPercent: number;
  startValue: number;
  endValue: number;
  startDate: string;
  endDate: string;
}

export interface GoalContributionData {
  goalId: string;
  monthlyContributions: Array<{
    month: string;
    amount: number;
    cumulativeAmount: number;
  }>;
  totalContributions: number;
  averageMonthlyContribution: number;
  contributionTrend: 'increasing' | 'decreasing' | 'stable';
}

interface GoalPerformanceRow {
  snapshot_date: string;
  total_value: number;
  gains_losses?: number;
  daily_change_percentage?: number;
}

interface TransactionRow {
  transaction_date: string;
  amount: number;
  // Add other transaction fields as needed
}

interface HoldingRow {
  symbol: string;
  name?: string;
  market_value?: number;
  asset_type: AssetType;
  logo_url?: string;
}

export interface GoalProjection {
  goalId: string;
  currentAmount: number;
  targetAmount: number;
  monthlyContribution: number;
  projectedCompletionDate: string;
  monthsToCompletion: number;
  requiredMonthlyContribution: number;
  onTrack: boolean;
}

export class GoalAnalyticsService {
  private static instance: GoalAnalyticsService;

  private constructor() {}

  static getInstance(): GoalAnalyticsService {
    if (!GoalAnalyticsService.instance) {
      GoalAnalyticsService.instance = new GoalAnalyticsService();
    }
    return GoalAnalyticsService.instance;
  }

  // ============================================================================
  // Goal Analytics Dashboard
  // ============================================================================

  /**
   * Get comprehensive analytics for a specific goal
   * Requirements: 5.1 - goal progress visualization and metrics
   */
  async getGoalAnalytics(
    userId: string,
    goalId: string
  ): Promise<{ analytics?: GoalAnalytics; error?: AuthError }> {
    try {
      // Get goal summary with calculated values
      const { data: goalData, error: goalError } = await supabase
        .from(TABLES.GOAL_SUMMARY)
        .select('*')
        .eq('user_id', userId)
        .eq('id', goalId)
        .single();

      if (goalError) {
        return {
          error: {
            code: 'DATABASE_ERROR',
            message: 'Failed to fetch goal data',
            details: goalError as unknown as Record<string, unknown>,
          },
        };
      }

      if (!goalData) {
        return {
          error: {
            code: 'NOT_FOUND',
            message: 'Goal not found',
          },
        };
      }

      // Use the calculated values from the database view
      const totalValue = goalData.calculated_current_amount || 0;
      const accountCount = goalData.account_count || 0;

      // Calculate contributions (simplified - in production would track actual contributions)
      const totalContributions = totalValue; // Assuming all current value is contributions for now
      const totalGainLoss = 0; // Would calculate from performance data
      const totalGainLossPercentage =
        totalContributions > 0 ? (totalGainLoss / totalContributions) * 100 : 0;

      // Use database-calculated progress
      const progressToTarget = goalData.calculated_progress_percentage || 0;

      // Calculate monthly progress rate (simplified)
      const monthsActive = this.calculateMonthsActive(goalData.created_at);
      const monthlyProgressRate =
        monthsActive > 0 ? progressToTarget / monthsActive : 0;

      // Project completion date
      const projectedCompletionDate = this.calculateProjectedCompletion(
        totalValue,
        goalData.target_amount,
        goalData.monthly_contribution || 0
      );

      const analytics: GoalAnalytics = {
        goal: {
          ...goalData,
          calculatedCurrentAmount: totalValue,
          calculatedProgressPercentage:
            goalData.calculated_progress_percentage || 0,
          accountCount: accountCount,
          daysRemaining: goalData.days_remaining,
        },
        totalValue,
        totalContributions,
        totalGainLoss,
        totalGainLossPercentage,
        progressToTarget: goalData.calculated_progress_percentage || 0,
        monthlyProgressRate,
        projectedCompletionDate,
        accountCount: accountCount,
        lastUpdated: new Date().toISOString(),
      };

      return { analytics };
    } catch (error) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: 'An unexpected error occurred while fetching goal analytics',
          details: error as unknown as Record<string, unknown>,
        },
      };
    }
  }

  /**
   * Get performance data for a goal over time
   * Requirements: 5.3 - historical data display
   */
  async getGoalPerformance(
    userId: string,
    goalId: string,
    timeRange: '1D' | '1W' | '1M' | '3M' | '6M' | '1Y' | 'ALL'
  ): Promise<{ performance?: GoalPerformanceData; error?: AuthError }> {
    try {
      // Get current goal value
      const { analytics, error: analyticsError } = await this.getGoalAnalytics(
        userId,
        goalId
      );

      if (analyticsError || !analytics) {
        return { error: analyticsError };
      }

      const currentValue = analytics.totalValue;

      // Get historical performance data from goal_performance table
      const { data: performanceData, error: performanceError } = await supabase
        .from(TABLES.GOAL_PERFORMANCE)
        .select('*')
        .eq('goal_id', goalId)
        .gte('snapshot_date', this.getStartDateForRange(timeRange))
        .order('snapshot_date', { ascending: true });

      if (performanceError) {
        return {
          error: {
            code: 'DATABASE_ERROR',
            message: 'Failed to fetch performance data',
            details: performanceError as unknown as Record<string, unknown>,
          },
        };
      }

      // If no historical data, generate mock data for demonstration
      let data: PerformanceDataPoint[];
      if (!performanceData || performanceData.length === 0) {
        data = this.generateMockPerformanceData(currentValue, timeRange);
      } else {
        data = performanceData.map((point: GoalPerformanceRow) => ({
          date: point.snapshot_date,
          value: point.total_value,
          gainLoss: point.gains_losses || 0,
          gainLossPercent: point.daily_change_percentage || 0,
        }));
      }

      const startValue = data.length > 0 ? data[0].value : currentValue;
      const endValue = currentValue;
      const totalReturn = endValue - startValue;
      const totalReturnPercent =
        startValue > 0 ? (totalReturn / startValue) * 100 : 0;

      const performance: GoalPerformanceData = {
        goalId,
        timeRange,
        data,
        totalReturn,
        totalReturnPercent,
        startValue,
        endValue,
        startDate: data.length > 0 ? data[0].date : new Date().toISOString(),
        endDate: new Date().toISOString(),
      };

      return { performance };
    } catch (error) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message:
            'An unexpected error occurred while fetching goal performance',
          details: error as unknown as Record<string, unknown>,
        },
      };
    }
  }

  /**
   * Get contribution tracking data for a goal
   * Requirements: 5.1 - contribution tracking
   */
  async getGoalContributions(
    goalId: string
  ): Promise<{ contributions?: GoalContributionData; error?: AuthError }> {
    try {
      // Get transactions for all accounts in this goal
      const { data: transactionData, error: transactionError } = await supabase
        .from(TABLES.TRANSACTIONS)
        .select(
          `
          *,
          accounts!inner(goal_id)
        `
        )
        .eq('accounts.goal_id', goalId)
        .in('transaction_type', ['deposit', 'buy'])
        .order('transaction_date', { ascending: true });

      if (transactionError) {
        return {
          error: {
            code: 'DATABASE_ERROR',
            message: 'Failed to fetch contribution data',
            details: transactionError as unknown as Record<string, unknown>,
          },
        };
      }

      const transactions = transactionData || [];

      // Group transactions by month
      const monthlyContributions = new Map<string, number>();
      let totalContributions = 0;

      transactions.forEach((transaction: TransactionRow) => {
        const month = transaction.transaction_date.substring(0, 7); // YYYY-MM
        const amount = Math.abs(transaction.amount); // Ensure positive for contributions

        monthlyContributions.set(
          month,
          (monthlyContributions.get(month) || 0) + amount
        );
        totalContributions += amount;
      });

      // Convert to array format with cumulative amounts
      const monthlyData: Array<{
        month: string;
        amount: number;
        cumulativeAmount: number;
      }> = [];

      let cumulativeAmount = 0;
      Array.from(monthlyContributions.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .forEach(([month, amount]) => {
          cumulativeAmount += amount;
          monthlyData.push({
            month,
            amount,
            cumulativeAmount,
          });
        });

      // Calculate average and trend
      const averageMonthlyContribution =
        monthlyData.length > 0 ? totalContributions / monthlyData.length : 0;

      const contributionTrend = this.calculateContributionTrend(monthlyData);

      const contributions: GoalContributionData = {
        goalId,
        monthlyContributions: monthlyData,
        totalContributions,
        averageMonthlyContribution,
        contributionTrend,
      };

      return { contributions };
    } catch (error) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message:
            'An unexpected error occurred while fetching contribution data',
          details: error as unknown as Record<string, unknown>,
        },
      };
    }
  }

  /**
   * Get goal projections and completion estimates
   * Requirements: 5.1 - projections
   */
  async getGoalProjections(
    userId: string,
    goalId: string
  ): Promise<{ projections?: GoalProjection; error?: AuthError }> {
    try {
      const { analytics, error } = await this.getGoalAnalytics(userId, goalId);

      if (error || !analytics) {
        return { error };
      }

      const currentAmount = analytics.totalValue;
      const targetAmount = analytics.goal.targetAmount;
      const monthlyContribution = analytics.goal.monthlyContribution || 0;

      // Calculate months to completion
      const remainingAmount = targetAmount - currentAmount;
      const monthsToCompletion =
        monthlyContribution > 0
          ? Math.ceil(remainingAmount / monthlyContribution)
          : Infinity;

      // Calculate projected completion date
      const projectedCompletionDate =
        monthlyContribution > 0
          ? new Date(
              Date.now() + monthsToCompletion * 30 * 24 * 60 * 60 * 1000
            ).toISOString()
          : '';

      // Calculate required monthly contribution to meet target date
      let requiredMonthlyContribution = monthlyContribution;
      if (analytics.goal.targetDate) {
        const targetDate = new Date(analytics.goal.targetDate);
        const monthsRemaining = Math.max(
          1,
          Math.ceil(
            (targetDate.getTime() - Date.now()) / (30 * 24 * 60 * 60 * 1000)
          )
        );
        requiredMonthlyContribution = remainingAmount / monthsRemaining;
      }

      // Determine if on track
      const onTrack = analytics.goal.targetDate
        ? new Date(projectedCompletionDate) <=
          new Date(analytics.goal.targetDate)
        : true;

      const projections: GoalProjection = {
        goalId,
        currentAmount,
        targetAmount,
        monthlyContribution,
        projectedCompletionDate,
        monthsToCompletion:
          monthsToCompletion === Infinity ? 0 : monthsToCompletion,
        requiredMonthlyContribution,
        onTrack,
      };

      return { projections };
    } catch (error) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: 'An unexpected error occurred while calculating projections',
          details: error as unknown as Record<string, unknown>,
        },
      };
    }
  }

  /**
   * Get asset allocation for a goal across all its accounts
   * Requirements: 5.1 - goal progress visualization
   */
  async getGoalAssetAllocation(
    userId: string,
    goalId: string
  ): Promise<{ allocation: AssetAllocation[]; error?: AuthError }> {
    try {
      // First, get account IDs for this goal and user
      const { data: accountsData, error: accountsError } = await supabase
        .from(TABLES.ACCOUNTS)
        .select('id')
        .eq('goal_id', goalId)
        .eq('user_id', userId)
        .eq('is_active', true);

      if (accountsError) {
        return {
          allocation: [],
          error: {
            code: 'DATABASE_ERROR',
            message: 'Failed to fetch accounts data',
            details: accountsError as unknown as Record<string, unknown>,
          },
        };
      }

      const accountIds = accountsData?.map(account => account.id) || [];

      if (accountIds.length === 0) {
        return { allocation: [] };
      }

      // Get all holdings for these accounts
      const { data: holdingsData, error: holdingsError } = await supabase
        .from(TABLES.HOLDINGS_WITH_SECURITIES)
        .select('*')
        .in('account_id', accountIds);

      if (holdingsError) {
        return {
          allocation: [],
          error: {
            code: 'DATABASE_ERROR',
            message: 'Failed to fetch holdings data',
            details: holdingsError as unknown as Record<string, unknown>,
          },
        };
      }

      const holdings = holdingsData || [];
      const totalValue = holdings.reduce(
        (sum: number, holding: HoldingRow) => sum + (holding.market_value || 0),
        0
      );

      if (totalValue === 0) {
        return { allocation: [] };
      }

      const allocationMap = holdings
        .filter(
          (holding: HoldingRow) =>
            holding.market_value && holding.market_value > 0
        )
        .reduce(
          (acc: Record<string, AssetAllocation>, holding: HoldingRow) => {
            const ticker = holding.symbol;
            if (acc[ticker]) {
              // Aggregate holdings with the same ticker
              acc[ticker].value += holding.market_value!;
              acc[ticker].percentage = (acc[ticker].value / totalValue) * 100;
            } else {
              // Create new entry for this ticker
              acc[ticker] = {
                ticker: holding.symbol,
                name: holding.name || holding.symbol,
                value: holding.market_value!,
                percentage: (holding.market_value! / totalValue) * 100,
                sector: this.getSectorForSymbol(holding.symbol),
                type: holding.asset_type as
                  | 'stock'
                  | 'etf'
                  | 'mutual_fund'
                  | 'bond'
                  | 'other',
                logoUrl: holding.logo_url,
              };
            }
            return acc;
          },
          {} as Record<string, AssetAllocation>
        );

      // Convert to array and sort
      const allocation: AssetAllocation[] = (
        Object.values(allocationMap) as AssetAllocation[]
      ).sort((a: AssetAllocation, b: AssetAllocation) => b.value - a.value);

      return { allocation };
    } catch (error) {
      return {
        allocation: [],
        error: {
          code: 'UNKNOWN_ERROR',
          message:
            'An unexpected error occurred while calculating asset allocation',
          details: error as unknown as Record<string, unknown>,
        },
      };
    }
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  private calculateMonthsActive(createdAt: string): number {
    const created = new Date(createdAt);
    const now = new Date();
    return Math.max(
      1,
      Math.ceil(
        (now.getTime() - created.getTime()) / (30 * 24 * 60 * 60 * 1000)
      )
    );
  }

  private calculateProjectedCompletion(
    currentAmount: number,
    targetAmount: number,
    monthlyContribution: number
  ): string | undefined {
    if (monthlyContribution <= 0 || currentAmount >= targetAmount) {
      return undefined;
    }

    const remainingAmount = targetAmount - currentAmount;
    const monthsToCompletion = Math.ceil(remainingAmount / monthlyContribution);
    const completionDate = new Date();
    completionDate.setMonth(completionDate.getMonth() + monthsToCompletion);

    return completionDate.toISOString();
  }

  private getStartDateForRange(timeRange: string): string {
    const now = new Date();
    let daysBack: number;

    switch (timeRange) {
      case '1D':
        daysBack = 1;
        break;
      case '1W':
        daysBack = 7;
        break;
      case '1M':
        daysBack = 30;
        break;
      case '3M':
        daysBack = 90;
        break;
      case '6M':
        daysBack = 180;
        break;
      case '1Y':
        daysBack = 365;
        break;
      case 'ALL':
        daysBack = 365 * 10; // 10 years back
        break;
      default:
        daysBack = 30;
    }

    const startDate = new Date(now.getTime() - daysBack * 24 * 60 * 60 * 1000);
    return startDate.toISOString().split('T')[0]; // Return YYYY-MM-DD format
  }

  private generateMockPerformanceData(
    currentValue: number,
    timeRange: '1D' | '1W' | '1M' | '3M' | '6M' | '1Y' | 'ALL'
  ): PerformanceDataPoint[] {
    const points: PerformanceDataPoint[] = [];
    const now = new Date();
    let days: number;
    let interval: number;

    switch (timeRange) {
      case '1D':
        days = 1;
        interval = 1;
        break;
      case '1W':
        days = 7;
        interval = 1;
        break;
      case '1M':
        days = 30;
        interval = 1;
        break;
      case '3M':
        days = 90;
        interval = 3;
        break;
      case '6M':
        days = 180;
        interval = 7;
        break;
      case '1Y':
        days = 365;
        interval = 14;
        break;
      case 'ALL':
        days = 730;
        interval = 30;
        break;
    }

    const startValue = currentValue * (0.7 + Math.random() * 0.6); // ±30% variation
    let previousValue = startValue;

    for (let i = days; i >= 0; i -= interval) {
      const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);

      const volatility = 0.015; // 1.5% volatility
      const change = (Math.random() - 0.5) * 2 * volatility;
      const value = i === 0 ? currentValue : previousValue * (1 + change);

      const gainLoss = value - startValue;
      const gainLossPercent =
        startValue > 0 ? (gainLoss / startValue) * 100 : 0;

      points.push({
        date: date.toISOString(),
        value,
        gainLoss,
        gainLossPercent,
      });

      previousValue = value;
    }

    return points;
  }

  private calculateContributionTrend(
    monthlyData: Array<{
      month: string;
      amount: number;
      cumulativeAmount: number;
    }>
  ): 'increasing' | 'decreasing' | 'stable' {
    if (monthlyData.length < 3) {
      return 'stable';
    }

    const recent = monthlyData.slice(-3);
    const amounts = recent.map(d => d.amount);

    const increasing = amounts[2] > amounts[1] && amounts[1] > amounts[0];
    const decreasing = amounts[2] < amounts[1] && amounts[1] < amounts[0];

    if (increasing) return 'increasing';
    if (decreasing) return 'decreasing';
    return 'stable';
  }

  private getSectorForSymbol(symbol: string): string {
    // Mock sector mapping - in production would use real financial data API
    const sectorMapping: Record<string, string> = {
      AAPL: 'Technology',
      GOOGL: 'Technology',
      MSFT: 'Technology',
      TSLA: 'Consumer Discretionary',
      SPY: 'Diversified',
      VTI: 'Diversified',
      QQQ: 'Technology',
      BND: 'Fixed Income',
      VTIAX: 'International',
    };

    return sectorMapping[symbol] || 'Other';
  }
}

// Export singleton instance
export const goalAnalyticsService = GoalAnalyticsService.getInstance();
